import { useQueries, useQuery } from '@tanstack/react-query';
import type { PlaylistItem } from '@ghostscreen/contracts';
import { api } from '../../lib/api';

export type PlaylistMediaMembership = {
  playlistId: string;
  playlistName: string;
  itemId: string | null;
};

/**
 * Resolves which media assets appear in which playlists (draft items).
 * Powers unused dots + lightbox "Show in playlist" toggles.
 */
export function usePlaylistMediaUsage(organisationId: string) {
  const playlistsQuery = useQuery({
    queryKey: ['playlists', organisationId],
    queryFn: () => api.listPlaylists(organisationId),
  });

  const playlists = playlistsQuery.data ?? [];

  const itemQueries = useQueries({
    queries: playlists.map((playlist) => ({
      queryKey: ['playlist-items', playlist.id],
      queryFn: () => api.listPlaylistItems(organisationId, playlist.id),
    })),
  });

  const usedMediaIds = new Set<string>();
  for (const query of itemQueries) {
    for (const item of query.data ?? []) {
      usedMediaIds.add(item.mediaAssetId);
    }
  }

  const membershipFor = (mediaAssetId: string): PlaylistMediaMembership[] =>
    playlists.map((playlist, index) => {
      const items: PlaylistItem[] = itemQueries[index]?.data ?? [];
      const match = items.find((item) => item.mediaAssetId === mediaAssetId) ?? null;
      return {
        playlistId: playlist.id,
        playlistName: playlist.name,
        itemId: match?.id ?? null,
      };
    });

  const isPending =
    playlistsQuery.isPending ||
    (playlists.length > 0 && itemQueries.some((query) => query.isPending));

  return {
    playlists,
    usedMediaIds,
    membershipFor,
    isPending,
    isError: playlistsQuery.isError || itemQueries.some((query) => query.isError),
  };
}
